import Vue from 'vue'
import { testService } from '../_services'
import { renameObjKey } from '@/_helpers/rename-key.js'

const state = {
  testsMinimalFrom: -1,
  sharingUsers: {},
  searchedUsers: {},
  sharedTest: {},
  summary: {},
  annotations: {},
  newAnnotation: {},
  editAnnotation: {},
  removeAnnotation: {},
  ecgTestData: {},
  bpTestData: {},
  stats: {
    item: {
      red: 0,
      green: 0,
      yellow: 0
    }
  }
}

const actions = {
  sharingUsers ({ commit }, {queryName, label, page}) {
    commit('sharingUsersRequest', {queryName, label})

    testService.sharingUsers(queryName, label, page)
      .then(
        sharingUsers => commit('sharingUsersSuccess', {sharingUsers, queryName, label}),
        error => commit('sharingUsersFailure', {error, queryName, label})
      )
  },
  clearTestProfile ({ commit }) {
    commit('testProfileClear')
  },
  testProfile ({ commit }, userUuid) {
    commit('testProfileRequest')

    testService.testProfile(userUuid)
      .then(
        sharedTest => commit('testProfileSuccess', sharedTest),
        error => commit('testProfileFailure', error)
      )
  },
  testSummary ({ commit }, {context, fromDate, patientUuid}) {
    commit('testSummaryRequest')

    testService.testSummary(context, fromDate, patientUuid)
      .then(
        summary => commit('testSummarySuccess', summary),
        error => commit('testSummaryFailure', error)
      )
  },
  annotations ({ commit }, {userUuid, fromDate, toDate}) {
    commit('annotationsRequest')

    testService.annotations(userUuid, fromDate, toDate)
      .then(
        annotations => commit('annotationsSuccess', annotations),
        error => commit('annotationsFailure', error)
      )
  },
  newAnnotation ({ commit }, {userUuid, annotation}) {
    commit('newAnnotationRequest')

    testService.newAnnotation(userUuid, annotation)
      .then(
        annotation => commit('newAnnotationSuccess', annotation),
        error => commit('newAnnotationFailure', error)
      )
  },
  editAnnotation ({ commit }, {annotationUuid, annotation}) {
    if (state.editAnnotation.hasOwnProperty('loading') && state.editAnnotation.loading) {
      commit('editAnnotationFailure', 'another request is loading.')
      return
    }

    commit('editAnnotationRequest')

    testService.editAnnotation(annotationUuid, annotation)
      .then(
        annotation => commit('editAnnotationSuccess', annotation),
        error => commit('editAnnotationFailure', error)
      )
  },
  removeAnnotation ({ commit }, annotationUuid) {
    if (state.removeAnnotation.hasOwnProperty('loading') && state.removeAnnotation.loading) {
      commit('removeAnnotationFailure', 'another request is loading.')
      return
    }

    commit('removeAnnotationRequest')

    testService.removeAnnotation(annotationUuid)
      .then(
        annotation => commit('removeAnnotationSuccess', annotationUuid),
        error => commit('removeAnnotationFailure', error)
      )
  },
  testsMinimal ({ commit }, {userUuid, fromDate, toDate, type, resultFrom}) {
    commit('testsMinimalRequest', resultFrom)
    testService.tests(userUuid, fromDate, toDate, type, 'minimal')
      .then(
        tests => commit(`${type}testsMinimalSuccess`, tests),
        error => commit(`${type}testsMinimalFailure`, error)
      )
  },
  tests ({ commit }, {userUuid, fromDate, toDate, type, context}) {
    commit(`${type}TestsRequest`)
    testService.tests(userUuid, fromDate, toDate, type, context)
      .then(
        tests => commit(`${type}TestsSuccess`, tests),
        error => commit(`${type}TestsFailure`, error)
      )
  },
  testData ({ commit }, {uuid, type}) {
    commit(`${type}TestDataRequest`)
    testService.testData(uuid)
      .then(
        testData => commit(`${type}TestDataSuccess`, {uuid, testData}),
        error => commit(`${type}TestDataFailure`, error)
      )
  },
  editTestTags ({ commit }, {testUuid, tags}) {
    commit('editTestTagsRequest')

    testService.editTestTags(testUuid, tags)
      .then(
        _tagsResp => commit('editTestTagsSuccess', {testUuid, tags}),
        error => commit('editTestTagsFailure', error)
      )
  },
  userLabel ({ dispatch, commit }, {userUuid, label}) {
    commit('userLabelRequest')

    testService.userLabel(userUuid, label)
      .then(
        userLabel => commit('userLabelSuccess', {userUuid, label}),
        error => commit('userLabelFailure', error)
      )
  },
  stats ({ dispatch, commit }, userUuid) {
    commit('statsRequest')

    testService.stats(userUuid)
      .then(
        response => commit('statsSuccess', response),
        error => commit('statsFailure', error)
      )
  }
}

const mutations = {
  sharingUsersRequest (state, {queryName, label}) {
    if (label && label.length > 0) {
      return
    }
    if (queryName && queryName.length > 0) {
      state.searchedUsers = { loading: true }
    } else {
      state.sharingUsers = { loading: true }
    }
  },
  sharingUsersSuccess (state, {sharingUsers, queryName, label}) {
    // we requested to get more users from specific label
    if (label && label.length > 0) {
      if (state.sharingUsers.hasOwnProperty('items')) {
        sharingUsers = sharingUsers.filter(u => {
          return !state.sharingUsers.items.some(user => user.uuid === u.uuid)
        })
        state.sharingUsers.items.push(...sharingUsers)
      }
      return
    }

    if (queryName && queryName.length > 0) {
      state.searchedUsers = { items: sharingUsers }
    } else {
      state.sharingUsers = { items: sharingUsers }
    }
  },
  sharingUsersFailure (state, {error, queryName, label}) {
    if (label && label.length > 0) {
      return
    }

    if (queryName && queryName.length > 0) {
      state.searchedUsers = { error: error }
    } else {
      state.sharingUsers = { error: error }
    }
  },
  testProfileClear (state) {
  },
  testProfileRequest (state) {
    state.sharedTest = { loading: true }
  },
  testProfileSuccess (state, sharedTest) {
    state.sharedTest = { item: sharedTest }
  },
  testProfileFailure (state, error) {
    state.sharedTest = { error: error }
  },
  testSummaryRequest (state) {
    state.summary = { loading: true }
  },
  testSummarySuccess (state, summary) {
    state.summary = { item: summary }
  },
  testSummaryFailure (state, error) {
    state.summary = { error: error }
  },
  annotationsRequest (state) {
    state.annotations = { loading: true }
  },
  annotationsSuccess (state, annotations) {
    state.annotations = { items: annotations }
  },
  annotationsFailure (state, error) {
    state.annotations = { error: error }
  },
  newAnnotationRequest (state) {
    state.newAnnotation = { loading: true }
  },
  newAnnotationSuccess (state, annotation) {
    state.newAnnotation = { }
  },
  newAnnotationFailure (state, error) {
    state.newAnnotation = { error: error }
  },
  editAnnotationRequest (state) {
    state.editAnnotation = { loading: true }
  },
  editAnnotationSuccess (state, annotation) {
    state.editAnnotation = { }

    if (state.sharedTest.hasOwnProperty('item')) {
      let idx = -1
      state.sharedTest.item.last_annotations.forEach((elem, index) => {
        if (elem.uuid === annotation.uuid) {
          idx = index
        }
      })
      if (idx >= 0) {
        Vue.set(state.sharedTest.item.last_annotations, idx, annotation)
      }
    }
    if (state.annotations.hasOwnProperty('items')) {
      let idx = -1
      state.annotations.items.forEach((elem, index) => {
        if (elem.uuid === annotation.uuid) {
          idx = index
        }
      })
      if (idx >= 0) {
        Vue.set(state.annotations.items, idx, annotation)
      }
    }
  },
  editAnnotationFailure (state, error) {
    state.editAnnotation = { error: error }
  },
  removeAnnotationRequest (state) {
    state.removeAnnotation = { loading: true }
  },
  removeAnnotationSuccess (state, annotationUuid) {
    state.removeAnnotation = { }

    if (state.sharedTest.hasOwnProperty('item')) {
      state.sharedTest.item.last_annotations = state.sharedTest.item.last_annotations.filter(elem => {
        if (elem.uuid === annotationUuid) {
          return false
        }
        return true
      })
    }
    if (state.annotations.hasOwnProperty('items')) {
      state.annotations.items = state.annotations.items.filter(elem => {
        if (elem.uuid === annotationUuid) {
          return false
        }
        return true
      })
    }
  },
  removeAnnotationFailure (state, error) {
    state.removeAnnotation = { error: error }
  },
  testsMinimalRequest (state, resultFrom) {
    state.testsMinimal = { loading: true }
    state.testsMinimalFrom = resultFrom
  },
  bptestsMinimalSuccess (state, tests) {
    state.bptestsMinimal = { items: tests }
  },
  bptestsMinimalFailure (state, error) {
    state.bptestsMinimal = { error: error }
  },
  ecgtestsMinimalSuccess (state, tests) {
    state.ecgtestsMinimal = { items: tests }
  },
  ecgtestsMinimalFailure (state, error) {
    state.ecgtestsMinimal = { error: error }
  },
  bpTestsRequest (state) {
    state.bpTests = { loading: true }
  },
  bpTestsSuccess (state, tests) {
    state.bpTests = { items: tests }
  },
  bpTestsFailure (state, error) {
    state.bpTests = { error: error }
  },
  ecgTestsRequest (state) {
    state.ecgTests = { loading: true }
  },
  ecgTestsSuccess (state, tests) {
    state.ecgTests = { items: tests }
  },
  ecgTestsFailure (state, error) {
    state.ecgTests = { error: error }
  },
  bpTestDataRequest (state) {
    state.bpTestData = { loading: true }
  },
  // eslint-disable-next-line no-unused-vars
  bpTestDataSuccess (state, {uuid, testData}) {
    renameObjKey(testData, 'a', 'hr')
    renameObjKey(testData, 'b', 'sys')
    renameObjKey(testData, 'c', 'dia')
    renameObjKey(testData, 'd', 'arr')

    state.bpTestData = { }
  },
  bpTestDataFailure (state, error) {
    state.bpTestData = { error: error }
  },
  ecgTestDataRequest (state) {
    state.ecgTestData = { loading: true }
  },
  // eslint-disable-next-line no-unused-vars
  ecgTestDataSuccess (state, {uuid, testData}) {
    renameObjKey(testData, 'a', 'test_start')
    renameObjKey(testData, 'b', 'test_end')
    renameObjKey(testData, 'e', 'lead1')
    renameObjKey(testData, 'f', 'lead2')
    renameObjKey(testData, 'g', 'comment')
    renameObjKey(testData, 'h', 'product_type')

    state.ecgTestData = { }
  },
  ecgTestDataFailure (state, error) {
    state.ecgTestData = { error: error }
  },
  editTestTagsRequest (state) {
    state.testTags = { loading: true }
  },
  editTestTagsSuccess (state, {testUuid, tags}) {
    state.testTags = { }

    if (state.sharedTest.hasOwnProperty('item')) {
      state.sharedTest.item.last_tests = state.sharedTest.item.last_tests.map(elem => {
        if (elem.uuid === testUuid) {
          elem.tags = tags
        }
        return elem
      })
    }

    if (state.ecgTests && state.ecgTests.hasOwnProperty('item')) {
      state.ecgTests = state.ecgTests.map(elem => {
        if (elem.uuid === testUuid) {
          elem.tags = tags
        }
        return elem
      })
    }

    if (state.bpTests && state.bpTests.hasOwnProperty('item')) {
      state.bpTests = state.bpTests.map(elem => {
        if (elem.uuid === testUuid) {
          elem.tags = tags
        }
        return elem
      })
    }
  },
  editTestTagsFailure (state, error) {
    state.testTags = { error: error }
  },
  userLabelRequest (state) {
    state.userLabel = { loading: true }
  },
  userLabelSuccess (state, {userUuid, label}) {
    state.userLabel = { item: label }
    // update users list
    if (state.sharingUsers.items) {
      let from
      let to = label
      let changed = false
      state.sharingUsers.items = state.sharingUsers.items.map(user => {
        if (user.uuid === userUuid) {
          changed = true
          from = user.label
          user.label = label
        }
        return user
      })

      if (changed && state.stats && state.stats.item) {
        state.stats.item[from] -= 1
        state.stats.item[to] += 1
      }
    }
  },
  userLabelFailure (state, error) {
    state.userLabel = { error: error }
  },
  statsRequest (state) {
    state.stats = { loading: true }
  },
  statsSuccess (state, stats) {
    state.stats = { item: stats }
  },
  statsFailure (state, error) {
    state.stats = { error: error }
  }
}

export const test = {
  namespaced: true,
  state,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/_store/test.module.js